/**
 * Script Analytics (MA-IMPL-125)
 * 
 * Displays script-level performance metrics using the lineage chain:
 * appointment.script_id → client.script_id → revenue
 * 
 * Does NOT infer or backfill missing script_id values.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  TrendingUp,
  DollarSign,
  Users,
  Target,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Trophy,
  AlertTriangle,
  Zap,
  Filter,
  GitBranch,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { api } from '../../lib/api';
import { HELPER_DEFINITIONS } from './HelperText';

// =============================================================================
// API HOOKS
// =============================================================================

function useScriptPerformance({ period, sortBy, workspaceId, campaignId, vertical, channel, tenantId }) {
  return useQuery({
    queryKey: ['script-performance', period, sortBy, workspaceId, campaignId, vertical, channel, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period, sort_by: sortBy });
      if (workspaceId) params.append('workspace_id', workspaceId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (vertical && vertical !== 'all') params.append('vertical', vertical);
      if (channel && channel !== 'all') params.append('channel', channel);
      if (tenantId) params.append('tenant_id', tenantId);
      
      const res = await api.get(`/analytics/script-performance?${params}`);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

function useJourneyPerformance({ period, sortBy, workspaceId, campaignId, vertical, channel, tenantId }) {
  return useQuery({
    queryKey: ['journey-performance', period, sortBy, workspaceId, campaignId, vertical, channel, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period, sort_by: sortBy });
      if (workspaceId) params.append('workspace_id', workspaceId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (vertical && vertical !== 'all') params.append('vertical', vertical);
      if (channel && channel !== 'all') params.append('channel', channel);
      if (tenantId) params.append('tenant_id', tenantId);
      
      const res = await api.get(`/analytics/journey-performance?${params}`);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// FORMATTERS
// =============================================================================

function formatCurrency(value) {
  if (value === null || value === undefined) return '—';
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
}

function truncateId(id, maxLen = 12) {
  if (!id) return '—';
  if (id.length <= maxLen) return id;
  return `${id.substring(0, maxLen)}...`;
}

// =============================================================================
// SUMMARY CARD
// =============================================================================

function SummaryCard({ title, value, icon: Icon, color = 'indigo' }) {
  const colorClasses = {
    indigo: 'text-indigo-400 bg-indigo-500/10',
    green: 'text-emerald-400 bg-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
  };

  return (
    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-800/50">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">{title}</p>
          <p className="text-xl font-bold text-white mt-0.5">{value}</p>
        </div>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SCRIPT PERFORMANCE TABLE
// =============================================================================

function ScriptTable({ scripts, sortBy, onSort }) {
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return <ChevronDown className="w-3.5 h-3.5 inline ml-1" />;
  };

  const SortableHeader = ({ field, children, className = "" }) => (
    <TableHead 
      className={`text-slate-300 cursor-pointer hover:text-white transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      {children}
      <SortIcon field={field} />
    </TableHead>
  );

  if (scripts.length === 0) {
    return (
      <div className="py-10 text-center">
        <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <h4 className="text-base font-medium text-slate-300 mb-1">No Script Data</h4>
        <p className="text-slate-500 text-sm max-w-sm mx-auto">
          Script analytics appear when appointments have script_id lineage.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="text-slate-300 w-[200px]">Script</TableHead>
            <SortableHeader field="appointments" className="text-right">Appts</SortableHeader>
            <SortableHeader field="shows" className="text-right">Shows</SortableHeader>
            <SortableHeader field="clients" className="text-right">Clients</SortableHeader>
            <SortableHeader field="revenue" className="text-right">Revenue</SortableHeader>
            <SortableHeader field="appointment_to_show_rate" className="text-right">A→S</SortableHeader>
            <SortableHeader field="show_to_close_rate" className="text-right">S→C</SortableHeader>
            <SortableHeader field="revenue_per_appointment" className="text-right">Rev/Appt</SortableHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {scripts.map((script) => (
            <TableRow 
              key={script.script_id} 
              className={`border-slate-800/50 hover:bg-slate-800/30 ${
                script.is_strongest ? 'bg-emerald-500/5' : 
                script.is_weakest ? 'bg-amber-500/5' : ''
              }`}
            >
              <TableCell className="font-medium">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-100 truncate max-w-[150px]" title={script.script_name}>
                      {script.script_name}
                    </span>
                    {script.is_strongest && (
                      <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs py-0">
                        <Trophy className="w-3 h-3 mr-1" />Top
                      </Badge>
                    )}
                    {script.is_weakest && (
                      <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs py-0">
                        <AlertTriangle className="w-3 h-3 mr-1" />Low
                      </Badge>
                    )}
                  </div>
                  {(script.has_low_show_rate || script.has_low_close_rate) && (
                    <span className="text-xs text-amber-400">
                      {[
                        script.has_low_show_rate && 'Low show',
                        script.has_low_close_rate && 'Low close',
                      ].filter(Boolean).join(', ')}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">{script.appointments}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{script.shows}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{script.clients}</TableCell>
              <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(script.revenue)}</TableCell>
              <TableCell className={`text-right font-mono ${script.has_low_show_rate ? 'text-amber-400' : 'text-slate-100'}`}>
                {formatPercent(script.appointment_to_show_rate)}
              </TableCell>
              <TableCell className={`text-right font-mono ${script.has_low_close_rate ? 'text-amber-400' : 'text-slate-100'}`}>
                {formatPercent(script.show_to_close_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatCurrency(script.revenue_per_appointment)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// =============================================================================
// JOURNEY PERFORMANCE TABLE
// =============================================================================

function JourneyTable({ journeys, sortBy, onSort }) {
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return <ChevronDown className="w-3.5 h-3.5 inline ml-1" />;
  };

  const SortableHeader = ({ field, children, className = "" }) => (
    <TableHead 
      className={`text-slate-300 cursor-pointer hover:text-white transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      {children}
      <SortIcon field={field} />
    </TableHead>
  );

  if (journeys.length === 0) {
    return (
      <div className="py-10 text-center">
        <TrendingUp className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <h4 className="text-base font-medium text-slate-300 mb-1">No Journey Data</h4>
        <p className="text-slate-500 text-sm max-w-sm mx-auto">
          Journey analytics appear when leads have journey_id lineage from funnel sessions.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="text-slate-300 w-[180px]">Journey ID</TableHead>
            <SortableHeader field="leads" className="text-right">Leads</SortableHeader>
            <SortableHeader field="appointments" className="text-right">Appts</SortableHeader>
            <SortableHeader field="clients" className="text-right">Clients</SortableHeader>
            <SortableHeader field="revenue" className="text-right">Revenue</SortableHeader>
            <SortableHeader field="lead_to_appt" className="text-right">L→A</SortableHeader>
            <SortableHeader field="appt_to_client" className="text-right">A→C</SortableHeader>
            <SortableHeader field="revenue_per_lead" className="text-right">Rev/Lead</SortableHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {journeys.map((journey) => (
            <TableRow 
              key={journey.journey_id} 
              className={`border-slate-800/50 hover:bg-slate-800/30 ${
                journey.is_strongest ? 'bg-emerald-500/5' : 
                journey.is_weakest ? 'bg-amber-500/5' : ''
              }`}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <span className="text-slate-100 font-mono text-sm" title={journey.journey_id}>
                    {truncateId(journey.journey_id, 16)}
                  </span>
                  {journey.is_strongest && (
                    <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs py-0">
                      <Trophy className="w-3 h-3" />
                    </Badge>
                  )}
                  {journey.is_weakest && (
                    <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs py-0">
                      <AlertTriangle className="w-3 h-3" />
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">{journey.leads}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{journey.appointments}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{journey.clients}</TableCell>
              <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(journey.revenue)}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(journey.lead_to_appointment_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(journey.appointment_to_client_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatCurrency(journey.revenue_per_lead)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ScriptAnalytics({ 
  period = 'mtd', 
  workspaceId, 
  campaignId,
  vertical,
  channel,
  tenantId 
}) {
  const [activeTab, setActiveTab] = useState('scripts');
  const [scriptSortBy, setScriptSortBy] = useState('revenue');
  const [journeySortBy, setJourneySortBy] = useState('revenue');
  
  const scriptQuery = useScriptPerformance({
    period,
    sortBy: scriptSortBy,
    workspaceId,
    campaignId,
    vertical,
    channel,
    tenantId,
  });

  const journeyQuery = useJourneyPerformance({
    period,
    sortBy: journeySortBy,
    workspaceId,
    campaignId,
    vertical,
    channel,
    tenantId,
  });

  const scripts = scriptQuery.data?.scripts || [];
  const journeys = journeyQuery.data?.journeys || [];

  return (
    <div className="space-y-4" data-testid="script-analytics">
      {/* Header - MA-ARCH-136A: Enhanced with helper text */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-400" />
            Script & Journey Analytics
          </h3>
          {/* MA-ARCH-136A: Helper text for Scripts and Journeys */}
          <div className="flex flex-col gap-1 mt-1">
            <p className="text-xs text-slate-500">{HELPER_DEFINITIONS.scripts}</p>
            <p className="text-xs text-slate-500">{HELPER_DEFINITIONS.journeys}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/5 border-white/10">
          <TabsTrigger 
            value="scripts" 
            className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300"
            data-testid="scripts-tab"
          >
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            Scripts ({scripts.length})
          </TabsTrigger>
          <TabsTrigger 
            value="journeys" 
            className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300"
            data-testid="journeys-tab"
          >
            <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
            Journeys ({journeys.length})
          </TabsTrigger>
        </TabsList>

        {/* Scripts Tab */}
        <TabsContent value="scripts" className="mt-4">
          {/* Script Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <SummaryCard
              title="Scripts"
              value={scriptQuery.isLoading ? '...' : scriptQuery.data?.total_scripts || 0}
              icon={FileText}
              color="purple"
            />
            <SummaryCard
              title="Appointments"
              value={scriptQuery.isLoading ? '...' : scriptQuery.data?.total_appointments || 0}
              icon={Users}
              color="blue"
            />
            <SummaryCard
              title="Total Revenue"
              value={scriptQuery.isLoading ? '...' : formatCurrency(scriptQuery.data?.total_revenue || 0)}
              icon={DollarSign}
              color="green"
            />
            <SummaryCard
              title="Avg/Script"
              value={scriptQuery.isLoading ? '...' : formatCurrency(scriptQuery.data?.avg_revenue_per_script || 0)}
              icon={Target}
              color="amber"
            />
          </div>

          {/* Sort Control */}
          <div className="flex justify-end mb-3">
            <Select value={scriptSortBy} onValueChange={setScriptSortBy}>
              <SelectTrigger className="w-44 bg-slate-800 border-slate-700 h-8 text-sm">
                <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue">Revenue</SelectItem>
                <SelectItem value="clients">Clients</SelectItem>
                <SelectItem value="show_rate">Show Rate</SelectItem>
                <SelectItem value="close_rate">Close Rate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Script Table */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-0">
              {scriptQuery.isLoading ? (
                <div className="py-10 text-center text-slate-400">
                  <div className="animate-pulse">Loading scripts...</div>
                </div>
              ) : scriptQuery.error ? (
                <div className="py-10 text-center">
                  <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-300 font-medium text-sm">Script Analytics Unavailable</p>
                  <p className="text-slate-500 text-xs mt-1">Unable to load script performance data at this time.</p>
                </div>
              ) : (
                <ScriptTable 
                  scripts={scripts} 
                  sortBy={scriptSortBy}
                  onSort={setScriptSortBy}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Journeys Tab */}
        <TabsContent value="journeys" className="mt-4">
          {/* Journey Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <SummaryCard
              title="Journeys"
              value={journeyQuery.isLoading ? '...' : journeyQuery.data?.total_journeys || 0}
              icon={TrendingUp}
              color="blue"
            />
            <SummaryCard
              title="Total Leads"
              value={journeyQuery.isLoading ? '...' : journeyQuery.data?.total_leads || 0}
              icon={Users}
              color="indigo"
            />
            <SummaryCard
              title="Total Revenue"
              value={journeyQuery.isLoading ? '...' : formatCurrency(journeyQuery.data?.total_revenue || 0)}
              icon={DollarSign}
              color="green"
            />
            <SummaryCard
              title="Avg/Journey"
              value={journeyQuery.isLoading ? '...' : formatCurrency(journeyQuery.data?.avg_revenue_per_journey || 0)}
              icon={Target}
              color="amber"
            />
          </div>

          {/* Sort Control */}
          <div className="flex justify-end mb-3">
            <Select value={journeySortBy} onValueChange={setJourneySortBy}>
              <SelectTrigger className="w-44 bg-slate-800 border-slate-700 h-8 text-sm">
                <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue">Revenue</SelectItem>
                <SelectItem value="clients">Clients</SelectItem>
                <SelectItem value="leads">Leads</SelectItem>
                <SelectItem value="lead_to_appt">L→A Rate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Journey Table */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-0">
              {journeyQuery.isLoading ? (
                <div className="py-10 text-center text-slate-400">
                  <div className="animate-pulse">Loading journeys...</div>
                </div>
              ) : journeyQuery.error ? (
                <div className="py-10 text-center">
                  <GitBranch className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-300 font-medium text-sm">Journey Analytics Unavailable</p>
                  <p className="text-slate-500 text-xs mt-1">Unable to load journey performance data at this time.</p>
                </div>
              ) : (
                <JourneyTable 
                  journeys={journeys} 
                  sortBy={journeySortBy}
                  onSort={setJourneySortBy}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Data Note */}
      <div className="text-xs text-slate-500 text-center">
        * Uses existing lineage chain. Records without script_id/journey_id are excluded.
      </div>
    </div>
  );
}
